import { Building01, ChevronSelectorVertical } from "@untitledui/icons";
import { useLayoutEffect, useRef, useState } from "react";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from "react-aria-components";

import { AvatarLabelGroup } from "@/components/ui/avatar/avatar-label-group";
import { getInitials } from "@/components/ui/avatar/utils";
import { useOrgSelection } from "@/context/use-org-selection";
import { OrgSwitchPanel } from "@/features/navigation/components/org-switch-panel";
import { cx } from "@/utils/cx";

const OrgCardFace = ({
	isLoading,
	showChevron,
	subtitle,
	title,
}: {
	isLoading?: boolean;
	showChevron?: boolean;
	subtitle: string;
	title: string;
}) => (
	<>
		{isLoading ? (
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<div className="size-10 shrink-0 animate-pulse rounded-full bg-quaternary" />
				<div className="min-w-0 flex-1 space-y-1.5">
					<div className="h-3.5 w-28 animate-pulse rounded bg-quaternary" />
					<div className="h-3 w-20 animate-pulse rounded bg-quaternary" />
				</div>
			</div>
		) : (
			<AvatarLabelGroup
				initials={getInitials(title)}
				placeholderIcon={Building01}
				size="md"
				subtitle={subtitle}
				title={title}
			/>
		)}

		{showChevron && (
			<div className="ml-auto flex shrink-0 items-center justify-center text-fg-quaternary group-hover:text-fg-quaternary_hover">
				<ChevronSelectorVertical className="size-4" />
			</div>
		)}
	</>
);

const triggerClassName =
	"bg-primary ring-secondary hover:bg-primary_hover outline-focus-ring group relative flex w-full cursor-pointer items-start gap-3 rounded-xl p-3 text-left ring-1 ring-inset transition-colors duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2";

/**
 * Standalone org switcher for the sidebar (same card face as NavAccountCard,
 * org list only). Popover width matches the trigger so the two read as one block.
 */
export const NavOrgCard = () => {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [triggerWidth, setTriggerWidth] = useState<number>();
	const { isLoading, organizations, selected } = useOrgSelection();
	const canSwitch = organizations.length > 1;

	const title = selected?.name ?? "No organization";
	const subtitle = selected?.orgCode ?? (isLoading ? "Loading…" : "—");

	useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current) return;
		setTriggerWidth(triggerRef.current.offsetWidth);
	}, [isOpen]);

	if (!canSwitch) {
		return (
			<div className={cx(triggerClassName, "cursor-default hover:bg-primary")}>
				<OrgCardFace isLoading={isLoading} subtitle={subtitle} title={title} />
			</div>
		);
	}

	return (
		<AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
			<AriaButton className={triggerClassName} ref={triggerRef}>
				<OrgCardFace
					isLoading={isLoading}
					showChevron
					subtitle={subtitle}
					title={title}
				/>

				<AriaPopover
					className={({ isEntering, isExiting }) =>
						cx(
							"origin-(--trigger-anchor-point) will-change-transform",
							isEntering &&
								"duration-150 ease-out animate-in fade-in placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
							isExiting &&
								"duration-100 ease-in animate-out fade-out placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
						)
					}
					offset={8}
					placement="bottom"
					triggerRef={triggerRef}
				>
					<AriaDialog
						className="rounded-xl bg-secondary_alt py-1.5 shadow-lg ring ring-secondary_alt outline-hidden"
						style={triggerWidth ? { width: triggerWidth } : undefined}
					>
						<OrgSwitchPanel onClose={() => setIsOpen(false)} />
					</AriaDialog>
				</AriaPopover>
			</AriaButton>
		</AriaDialogTrigger>
	);
};
